// src/pages/AdminRentalPolicyPage.js
import React, { useEffect, useState } from 'react';
import api from '../api';
import './AdminRentalPolicyPage.css';

const AdminRentalPolicyPage = () => {
    const [rentalPolicy, setRentalPolicy] = useState({
        discountClosedDaysAndHolidays: false,
        allowOnlinePayment: false,
        allowInStorePayment: false,
        useNonrefundableFee: false,
        useNonrefundableFeePerItem: false,
        nonrefundableFeeAmount: 0,
        nonrefundableFeeWindowHours: 24,
        onlyCollectNonrefundableFeeOnline: false,
        useRentalInsurance: false,
        rentalInsurancePercentage: 0.0,
        rentalInsuranceDeductible: 0.0,
        rentalCoverageDeductiblePercentage: 0.0,
        chargeRentalInsuranceOffSubtotalBeforeDiscount: false,
        policyDescription: '',
        policyPdfUrl: ''
    });

    const [pdfFile, setPdfFile] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch the existing rental policy
    useEffect(() => {
        const fetchRentalPolicy = async () => {
            try {
                const response = await api.get('/store/rental-policy');
                setRentalPolicy(response.data);
            } catch (err) {
                console.error('Failed to fetch rental policy:', err);
                setError('Could not load rental policy.');
            } finally {
                setLoading(false);
            }
        };
        fetchRentalPolicy();
    }, []);

    // Handle ANY checkbox toggles
    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setRentalPolicy((prev) => {
            const newPolicy = { ...prev, [name]: checked };

            // If any of these are false => onlyCollectNonrefundableFeeOnline = false
            if (
                !newPolicy.useNonrefundableFee ||
                !newPolicy.allowOnlinePayment ||
                !newPolicy.allowInStorePayment
            ) {
                newPolicy.onlyCollectNonrefundableFeeOnline = false;
            }

            // If useNonrefundableFee is turned off => set useNonrefundableFeePerItem to false
            if (!newPolicy.useNonrefundableFee) {
                newPolicy.useNonrefundableFeePerItem = false;
            }

            if (!newPolicy.useRentalInsurance) {
                newPolicy.chargeRentalInsuranceOffSubtotalBeforeDiscount = false;
            }

            return newPolicy;
        });
    };

    // Handle numeric changes
    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        setRentalPolicy((prev) => ({
            ...prev,
            [name]: parseFloat(value),
        }));
    };

    // Handle integer changes
    const handleIntegerChange = (e) => {
        const { name, value } = e.target;
        setRentalPolicy((prev) => ({
            ...prev,
            [name]: parseInt(value, 10),
        }));
    };

    // plain‑text / markdown rental‑policy narrative
    const handleDescriptionChange = (e) => {
        const { value } = e.target;
        setRentalPolicy((prev) => ({ ...prev, policyDescription: value }));
    };

    const handleAgreementChange = (e) => {
        const { value } = e.target;
        setRentalPolicy((prev) => ({ ...prev, rentalAgreement: value }));
    };

    // Save the policy
    const handleSave = async () => {
        try {
            // Save JSON fields (including description)
            await api.put('/store/rental-policy', rentalPolicy);

            alert('Rental policy updated successfully!');
        } catch (err) {
            console.error('Failed to update rental policy:', err);
            alert('Failed to update rental policy.');
        }
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p style={{ color: 'red' }}>{error}</p>;

    // onlyCollectNonrefundableFeeOnline is disabled if ANY of these are false
    // i.e. we require all to be true in order to enable it
    const disableOnlyNRFeeOnline = !(
        rentalPolicy.useNonrefundableFee &&
        rentalPolicy.allowOnlinePayment &&
        rentalPolicy.allowInStorePayment
    );

    return (
        <div className="admin-rental-policy-container">
            <h1>Store Rental Policies</h1>

            <div className="policy-row">
                <label>
                    <input
                        type="checkbox"
                        name="discountClosedDaysAndHolidays"
                        checked={rentalPolicy.discountClosedDaysAndHolidays}
                        onChange={handleCheckboxChange}
                    />
                    <span> Discount Closed Days & Holidays</span>
                </label>
            </div>

            <div className="policy-row">
                <label>
                    <input
                        type="checkbox"
                        name="allowOnlinePayment"
                        checked={rentalPolicy.allowOnlinePayment}
                        onChange={handleCheckboxChange}
                    />
                    <span> Allow Online Payment</span>
                </label>
            </div>

            <div className="policy-row">
                <label>
                    <input
                        type="checkbox"
                        name="allowInStorePayment"
                        checked={rentalPolicy.allowInStorePayment}
                        onChange={handleCheckboxChange}
                    />
                    <span> Allow In-Store Payment</span>
                </label>
            </div>

            <div className="policy-row">
                <label>
                    <input
                        type="checkbox"
                        name="useNonrefundableFee"
                        checked={rentalPolicy.useNonrefundableFee}
                        onChange={handleCheckboxChange}
                    />
                    <span> Use Reservation Deposit</span>
                </label>
            </div>

            <div className="policy-row">
                <label>
                    <input
                        type="checkbox"
                        name="useNonrefundableFeePerItem"
                        checked={rentalPolicy.useNonrefundableFeePerItem}
                        onChange={handleCheckboxChange}
                        disabled={!rentalPolicy.useNonrefundableFee}
                    />
                    <span> Use Reservation Deposit Per Item</span>
                </label>
            </div>

            <div className="policy-row">
                <label>
                    <input
                        type="checkbox"
                        name="onlyCollectNonrefundableFeeOnline"
                        checked={rentalPolicy.onlyCollectNonrefundableFeeOnline}
                        onChange={handleCheckboxChange}
                        disabled={disableOnlyNRFeeOnline}
                    />
                    <span> Only collect reservation deposit online (remainder in-store)</span>
                </label>
            </div>

            <div className="policy-row">
                <label className="policy-label">
                    Reservation Deposit Amount:
                </label>
                <input
                    type="number"
                    step="0.01"
                    name="nonrefundableFeeAmount"
                    value={rentalPolicy.nonrefundableFeeAmount}
                    onChange={handleNumberChange}
                    className="policy-input"
                    disabled={!rentalPolicy.useNonrefundableFee}
                />
            </div>

            <div className="policy-row">
                <label className="policy-label">
                    Reservation Deposit Window (hours):
                </label>
                <input
                    type="number"
                    name="nonrefundableFeeWindowHours"
                    value={rentalPolicy.nonrefundableFeeWindowHours}
                    onChange={handleIntegerChange}
                    className="policy-input"
                    disabled={!rentalPolicy.useNonrefundableFee}
                />
            </div>

            <div className="policy-row">
                <label>
                    <input
                        type="checkbox"
                        name="useRentalInsurance"
                        onChange={handleCheckboxChange}
                        checked={rentalPolicy.useRentalInsurance}
                    />
                    <span> Use Rental Protection Coverage</span>
                </label>
            </div>

            <div className="policy-row">
                <label className="policy-label">
                    Rental Protection Coverage Percentage:
                </label>
                <input
                    type="number"
                    max="100.00"
                    min="0.00"
                    step="any"
                    name="rentalInsurancePercentage"
                    value={rentalPolicy.rentalInsurancePercentage}
                    onChange={handleNumberChange}
                    disabled={!rentalPolicy.useRentalInsurance}
                />
                <span className="symbol">%</span>
            </div>

            <div className="policy-row">
                <label className="policy-label">
                    Rental Protection Coverage Deductible:
                </label>
                <span className="symbol">$</span>
                <input
                    type="number"
                    min="0.00"
                    step="any"
                    name="rentalInsuranceDeductible"
                    value={rentalPolicy.rentalInsuranceDeductible}
                    onChange={handleNumberChange}
                    disabled={!rentalPolicy.useRentalInsurance}
                />
            </div>

            <div className="policy-row">
                <label className="policy-label">
                    Rental Protection Coverage Deductible Percentage:
                </label>
                <input
                    type="number"
                    max="100.00"
                    min="0.00"
                    step="any"
                    name="rentalCoverageDeductiblePercentage"
                    value={rentalPolicy.rentalCoverageDeductiblePercentage}
                    onChange={handleNumberChange}
                    disabled={!rentalPolicy.useRentalInsurance}
                />
                <span className="symbol">%</span>
            </div>

            <div className="policy-row">
                <label className="policy-label">
                    Charge Rental Protection Coverage Based Off Subtotal BEFORE Discount:
                </label>
                <input
                    type="checkbox"
                    name="chargeRentalInsuranceOffSubtotalBeforeDiscount"
                    checked={rentalPolicy.chargeRentalInsuranceOffSubtotalBeforeDiscount}
                    onChange={handleCheckboxChange}
                    disabled={!rentalPolicy.useRentalInsurance}
                />
            </div>

            {/* TEXT SECTION    */}
            <h2 className="section-heading">Rental Protection Policy</h2>
            <textarea
                className="policy-description-textarea"
                value={rentalPolicy.policyDescription}
                onChange={handleDescriptionChange}
                rows={10}
                placeholder={
                    'Use plain text or markdown.\nFor example:\n## Lost / Damaged / Stolen\nCustomer is liable for repair or replacement value minus any purchased Protection deductible.'
                }
            />

            {/* TEXT SECTION    */}
            <h2 className="section-heading">Rental Agreement</h2>
            <textarea
                className="policy-description-textarea"
                value={rentalPolicy.rentalAgreement}
                onChange={handleAgreementChange}
                rows={10}
                placeholder={
                    'Use plain text or markdown.\nFor example:\n## Late Returns\nA late fee equal to one full rental day is applied for every calendar day the item is late.'
                }
            />

            <button className="policy-save-button" onClick={handleSave}>
                Save Policy
            </button>
        </div>
    );
};

export default AdminRentalPolicyPage;